import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { petAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import PetAvatar from '../components/PetAvatar';
import PetCreationWizard from '../components/PetCreationWizard';
import toast from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  XMarkIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const Pets = () => {
  const [showWizard, setShowWizard] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const queryClient = useQueryClient();

  const { data: pets, isLoading: petsLoading } = useQuery('pets', petAPI.getPets);
  const { data: petTypes } = useQuery('petTypes', petAPI.getPetTypes);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // Note: Pet creation is now handled by the PetCreationWizard component

  const updatePetMutation = useMutation(
    ({ id, data }) => petAPI.updatePet(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pets');
        setShowEditModal(false);
        setEditingPet(null);
        reset();
        toast.success('Pet updated successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update pet');
      }
    }
  );

  const deletePetMutation = useMutation(petAPI.deletePet, {
    onSuccess: () => {
      queryClient.invalidateQueries('pets');
      toast.success('Pet removed successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to remove pet');
    }
  });

  const onSubmit = (data) => {
    const petData = {
      petTypeId: parseInt(data.petTypeId),
      name: data.name,
      breed: data.breed || null,
      age: data.age ? parseInt(data.age) : null,
      weight: data.weight ? parseFloat(data.weight) : null
    };

    if (editingPet) {
      updatePetMutation.mutate({ id: editingPet.id, data: petData });
    }
  };

  const handleEdit = (pet) => {
    setEditingPet(pet);
    reset({
      petTypeId: pet.pet_type_id,
      name: pet.name,
      breed: pet.breed || '',
      age: pet.age || '',
      weight: pet.weight || ''
    });
    setShowEditModal(true);
  };

  const handleDelete = (petId) => {
    if (window.confirm('Are you sure you want to remove this pet?')) {
      deletePetMutation.mutate(petId);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingPet(null);
    reset();
  };

  if (petsLoading) {
    return <LoadingSpinner text="Loading pets..." />;
  }

  const petsData = pets?.data || [];
  const petTypesData = petTypes?.data || [];

  return (
    <div className="p-6 bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 min-h-screen">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <SparklesIcon className="h-8 w-8 text-playful-blue animate-bounce-slow" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-playful-blue to-playful-green bg-clip-text text-transparent">
            My Furry Friends
          </h1>
          <SparklesIcon className="h-8 w-8 text-playful-green animate-bounce-slow" />
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="btn btn-primary hover:scale-105 transform transition-all duration-200 shadow-lg"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Pet
        </button>
      </div>

      {petsData.length === 0 ? (
        <div className="text-center py-16 bg-gradient-to-br from-white via-yellow-50 to-pink-50 rounded-2xl shadow-lg border-2 border-dashed border-playful-blue relative overflow-hidden">
          {/* Floating paw prints */}
          <div className="absolute top-4 left-8 text-2xl animate-bounce" style={{ animationDelay: '0s' }}>🐾</div>
          <div className="absolute top-8 right-12 text-xl animate-bounce" style={{ animationDelay: '0.5s' }}>🐾</div>
          <div className="absolute bottom-8 left-16 text-lg animate-bounce" style={{ animationDelay: '1s' }}>🐾</div>
          <div className="absolute bottom-12 right-8 text-2xl animate-bounce" style={{ animationDelay: '1.5s' }}>🐾</div>
          
          {/* Main content */}
          <div className="relative z-10">
            <div className="flex justify-center items-center space-x-4 mb-4">
              <div className="animate-float" style={{ animationDelay: '0s' }}>
                <PetAvatar petType="Dog" size="lg" animated={true} />
              </div>
              <div className="animate-float" style={{ animationDelay: '0.3s' }}>
                <PetAvatar petType="Cat" size="lg" animated={true} />
              </div>
              <div className="animate-float" style={{ animationDelay: '0.6s' }}>
                <PetAvatar petType="Bird" size="lg" animated={true} />
              </div>
            </div>
            <div className="flex justify-center items-center space-x-4 mb-4">
              <div className="animate-float" style={{ animationDelay: '0.9s' }}>
                <PetAvatar petType="Rabbit" size="lg" animated={true} />
              </div>
              <div className="animate-float" style={{ animationDelay: '1.2s' }}>
                <PetAvatar petType="Guinea Pig" size="lg" animated={true} />
              </div>
              <div className="animate-float" style={{ animationDelay: '1.5s' }}>
                <PetAvatar petType="Hamster" size="lg" animated={true} />
              </div>
            </div>
            {/* <h3 className="mt-6 text-2xl font-bold bg-gradient-to-r from-playful-blue via-purple-500 to-playful-green bg-clip-text text-transparent">
              Wow, such empty! Much lonely! 🐕
            </h3> */}
            {/* <p className="mt-4 text-lg text-gray-700 max-w-md mx-auto leading-relaxed">
              Your pet palace is ready for some furry inhabitants! 🏰✨
              <br />
              <span className="text-playful-blue font-semibold">Who will be your first training buddy?</span>
            </p> */}
            
            {/* Fun pet type suggestions */}
            {/* <div className="mt-6 flex justify-center flex-wrap gap-3 text-3xl max-w-md mx-auto">
              <span className="animate-pulse hover:scale-125 transform transition-all cursor-default" title="Dogs love training!">🐕</span>
              <span className="animate-pulse hover:scale-125 transform transition-all cursor-default" title="Cats are independent learners!" style={{ animationDelay: '0.2s' }}>🐱</span>
              <span className="animate-pulse hover:scale-125 transform transition-all cursor-default" title="Birds are smart cookies!" style={{ animationDelay: '0.4s' }}>🦜</span>
              <span className="animate-pulse hover:scale-125 transform transition-all cursor-default" title="Rabbits hop into action!" style={{ animationDelay: '0.6s' }}>🐰</span>
              <span className="animate-pulse hover:scale-125 transform transition-all cursor-default" title="Guinea pigs are gentle learners!" style={{ animationDelay: '0.8s' }}>🐹</span>
              <span className="animate-pulse hover:scale-125 transform transition-all cursor-default" title="Hamsters are quick learners!" style={{ animationDelay: '1s' }}>🐹</span>
            </div>
             */}
            <div className="mt-8 space-y-4">
              <button
                onClick={() => setShowWizard(true)}
                className="btn btn-primary hover:scale-105 transform transition-all duration-200 shadow-lg animate-pulse bg-gradient-to-r from-playful-blue to-purple-500 hover:from-purple-500 hover:to-playful-green"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                🎉 Add Your First Furry Friend!
              </button>
              {/* <p className="text-sm text-gray-500 italic">
                "A house is not a home without pawprints!" 🏡💕
              </p> */}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {petsData.map((pet, index) => (
            <div 
              key={pet.id} 
              className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl hover:scale-105 transform transition-all duration-300 hover:rotate-1"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <PetAvatar petType={pet.pet_type} size="lg" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 flex items-center">
                      {pet.name}
                      <span className="ml-2 text-lg">❤️</span>
                    </h3>
                    <p className="text-sm font-medium text-playful-blue">{pet.pet_type}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(pet)}
                    className="p-2 text-gray-400 hover:text-playful-blue hover:bg-blue-50 rounded-full transition-colors"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(pet.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-4 space-y-3">
                {pet.breed && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600 flex items-center">
                      🧬 Breed:
                    </span>
                    <span className="text-sm font-bold text-gray-900 bg-white px-2 py-1 rounded-full">
                      {pet.breed}
                    </span>
                  </div>
                )}
                {pet.age && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600 flex items-center">
                      🎂 Age:
                    </span>
                    <span className="text-sm font-bold text-gray-900 bg-white px-2 py-1 rounded-full">
                      {pet.age} months
                    </span>
                  </div>
                )}
                {pet.weight && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600 flex items-center">
                      ⚖️ Weight:
                    </span>
                    <span className="text-sm font-bold text-gray-900 bg-white px-2 py-1 rounded-full">
                      {pet.weight} kg
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-6">
                <Link
                  to={`/app/pets/${pet.id}`}
                  className="w-full bg-gradient-to-r from-playful-blue to-playful-green text-white font-medium py-3 px-4 rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 text-center block"
                >
                  🐾 View Training Progress
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pet Creation Wizard */}
      <PetCreationWizard 
        isOpen={showWizard} 
        onClose={() => setShowWizard(false)} 
      />

      {/* Edit Pet Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Pet
              </h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="form-label">Pet Type</label>
                <select
                  {...register('petTypeId', { required: 'Pet type is required' })}
                  className="form-input"
                >
                  <option value="">Select pet type</option>
                  {petTypesData.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {errors.petTypeId && (
                  <p className="mt-1 text-sm text-red-600">{errors.petTypeId.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Name</label>
                <input
                  {...register('name', { 
                    required: 'Name is required',
                    maxLength: { value: 100, message: 'Name too long' }
                  })}
                  type="text"
                  className="form-input"
                  placeholder="Pet's name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Breed (optional)</label>
                <input
                  {...register('breed', {
                    maxLength: { value: 100, message: 'Breed name too long' }
                  })}
                  type="text"
                  className="form-input"
                  placeholder="Breed"
                />
                {errors.breed && (
                  <p className="mt-1 text-sm text-red-600">{errors.breed.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Age (months)</label>
                  <input
                    {...register('age', {
                      min: { value: 0, message: 'Age must be positive' },
                      max: { value: 600, message: 'Age too high' }
                    })}
                    type="number"
                    className="form-input"
                    placeholder="Age"
                  />
                  {errors.age && (
                    <p className="mt-1 text-sm text-red-600">{errors.age.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Weight (kg)</label>
                  <input
                    {...register('weight', {
                      min: { value: 0.1, message: 'Weight must be positive' },
                      max: { value: 200, message: 'Weight too high' }
                    })}
                    type="number"
                    step="0.1"
                    className="form-input"
                    placeholder="Weight"
                  />
                  {errors.weight && (
                    <p className="mt-1 text-sm text-red-600">{errors.weight.message}</p>
                  )}
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={updatePetMutation.isLoading}
                  className="flex-1 btn btn-primary"
                >
                  {updatePetMutation.isLoading ? 'Saving...' : 'Update Pet'}
                </button>
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pets;